import { useCallback, useContext, useEffect, useState } from 'react';
import TargetsAppContext from '../TargetsAppContext';
import isDefined from '../../../../isDefined';
import UsersContext from '../../../../contexts/UsersContext';
import userTypeCheckers from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UsersProvider/userTypeCheckers';

const useUsersWithAccess = () => {
  const { app, updateApp } = useContext(TargetsAppContext);
  const { allUsers } = useContext(UsersContext);
  const [isLoading, setIsLoading] = useState(false);
  const [usersWithAccess, setUsersWithAccess] = useState<
    (UserManagement.PendingUser | UserManagement.SignedUpUser)[]
  >([]);

  const getUsersFromIds = useCallback(
    (userIds: string[]) =>
      userIds
        .map((uid) => allUsers.find((u) => u.id === uid))
        .filter(isDefined)
        .filter(userTypeCheckers.isSignedUpOrPendingUser),
    [allUsers],
  );

  useEffect(() => {
    const userIds = app.access.userIds || [];
    setUsersWithAccess(getUsersFromIds(userIds));
  }, [app.access.userIds, getUsersFromIds]);

  const onUserAdded = useCallback(
    async (user: UserManagement.PendingUser | UserManagement.SignedUpUser) => {
      if (isLoading) {
        return;
      }
      setIsLoading(true);
      const newUsers = [...(app.access.userIds || []), user.id];
      const newAccess = {
        ...app.access,
        userIds: newUsers,
      };
      const newApp = {
        ...app,
        access: newAccess,
      };
      setUsersWithAccess(getUsersFromIds(newUsers));
      return updateApp(newApp).then(() => {
        setIsLoading(false);
      });
    },
    [app, getUsersFromIds, isLoading, updateApp],
  );

  const onUserRemoved = useCallback(
    async (user: UserManagement.PendingUser | UserManagement.SignedUpUser) => {
      if (isLoading) {
        return;
      }
      setIsLoading(true);
      const newUsers = [...(app.access.userIds || [])].filter(
        (uid) => uid !== user.id,
      );
      const newAccess = {
        ...app.access,
        userIds: newUsers,
      };
      const newApp = {
        ...app,
        access: newAccess,
      };
      setUsersWithAccess(getUsersFromIds(newUsers));
      return updateApp(newApp).then(() => {
        setIsLoading(false);
      });
    },
    [app, getUsersFromIds, isLoading, updateApp],
  );

  return { usersWithAccess, onUserAdded, onUserRemoved, isLoading };
};

export default useUsersWithAccess;
